/**
 * The order's two release switches (V1 completion O), under the Release step's heading: "Review before release" and
 * "Deliver together". The words, the state and where to set each come from the backend (`release_review`,
 * `deliver_together`); this only lays them out. A switch the caller may not set, or whose order has no line left
 * to release, carries no URL and is not drawn.
 */
import { useId } from "react";
import type { ReleaseSwitchView } from "@shared-core/data/order-to-invoice-types";
import { Label } from "../../primitives/label";
import { Switch } from "../../primitives/switch";

export interface ReleaseSwitchesHandlers {
  busy: boolean;
  /** Turn "Review before release" on or off for this order. */
  onSetReview: (isOn: boolean) => void;
  /** Turn "Deliver together" on or off for this order. */
  onSetDeliverTogether: (isOn: boolean) => void;
}

function SwitchRow({
  view,
  busy,
  onChange,
}: {
  view: ReleaseSwitchView;
  busy: boolean;
  onChange: (isOn: boolean) => void;
}) {
  const id = useId();
  return (
    <div className="flex items-start gap-3">
      <Switch
        id={id}
        checked={view.is_on}
        disabled={busy}
        aria-describedby={`${id}-help`}
        onCheckedChange={onChange}
      />
      <div className="space-y-0.5">
        <Label htmlFor={id} className="font-medium">
          {view.label}
        </Label>
        <p id={`${id}-help`} className="text-xs text-muted-foreground">
          {view.help}
        </p>
        {view.note && (
          <p className="text-xs text-muted-foreground">{view.note}</p>
        )}
      </div>
    </div>
  );
}

export function ReleaseSwitches({
  review,
  deliverTogether,
  handlers,
}: {
  review: ReleaseSwitchView | null | undefined;
  deliverTogether: ReleaseSwitchView | null | undefined;
  handlers: ReleaseSwitchesHandlers;
}) {
  const shown = [
    review?.set_url ? { view: review, onChange: handlers.onSetReview } : null,
    deliverTogether?.set_url
      ? { view: deliverTogether, onChange: handlers.onSetDeliverTogether }
      : null,
  ].filter((row) => row !== null);
  if (shown.length === 0) return null;
  return (
    <section
      aria-label="Release switches"
      className="space-y-3 rounded-md border border-border/60 p-3 text-sm"
    >
      {shown.map(({ view, onChange }) => (
        <SwitchRow
          key={view.label}
          view={view}
          busy={handlers.busy}
          onChange={onChange}
        />
      ))}
    </section>
  );
}
